import React, { useState, useEffect } from 'react';
import { ChevronDown, Phone, Mail, MapPin, Star, Users, TrendingUp, Award, ArrowRight, Menu, X, Target, Zap, Clock, DollarSign, User, Calendar, CheckCircle, AlertCircle, FileText, Send, ArrowLeft, Upload } from 'lucide-react';

const App = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationStep, setApplicationStep] = useState(1);
  const [showTracker, setShowTracker] = useState(false);
  const [applicationData, setApplicationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    experience: '',
    motivation: '',
    availability: '',
    cv: null
  });
  const [applicationStatus, setApplicationStatus] = useState({
    step: 1,
    status: 'pending',
    applicationId: 'MSK-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
    submittedAt: new Date()
  });

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const opportunities = [
    {
      title: "Revenus Illimités",
      desc: "Système de rémunération attractive basé sur la performance. Plus vous réussissez, plus vous gagnez.",
      icon: <DollarSign className="w-8 h-8 text-green-400" />
    },
    {
      title: "Formation Premium",
      desc: "Programme de formation complet et personnalisé pour développer vos compétences entrepreneuriales.",
      icon: <Award className="w-8 h-8 text-yellow-400" />
    },
    {
      title: "Évolution Rapide",
      desc: "Opportunités d'avancement accélérées vers des postes de management et de leadership.",
      icon: <TrendingUp className="w-8 h-8 text-blue-400" />
    },
    {
      title: "Flexibilité Totale",
      desc: "Gérez votre emploi du temps et développez votre activité selon vos objectifs personnels.",
      icon: <Clock className="w-8 h-8 text-purple-400" />
    }
  ];

  const testimonials = [
    {
      name: "Sophie D.",
      role: "Représentante Senior",
      text: "Groupe Mosaik m'a donné toutes les clés pour réussir dans la vente. L'approche personnalisée avec chaque client fait vraiment la différence.",
      earnings: "Évolution rapide"
    },
    {
      name: "Marc L.",
      role: "Responsable Équipe",
      text: "L'écoute client et la réactivité de l'équipe sont exceptionnelles. Nous développons de vraies relations durables avec nos partenaires.",
      earnings: "Leadership"
    },
    {
      name: "Amélie R.",
      role: "Consultante Marketing",
      text: "Les technologies utilisées et la stratégie digitale nous permettent d'être toujours à la pointe. Une entreprise qui investit dans l'innovation.",
      earnings: "Innovation"
    }
  ];

  const benefits = [
    "Développement durable des relations clients",
    "Toujours à l'écoute du client",
    "Service très réactif aux besoins",
    "Travail collaboratif en équipe",
    "Technologies de pointe pour optimiser les résultats",
    "Formation continue en vente et service clientèle",
    "Environnement de travail dynamique à Montréal",
    "Solutions efficaces et rentables"
  ];

  const applicationSteps = [
    { id: 1, title: 'Candidature Reçue', desc: 'Votre dossier est en cours d\'examen' },
    { id: 2, title: 'Présélection', desc: 'Étude de votre profil par nos experts' },
    { id: 3, title: 'Entretien Téléphonique', desc: 'Premier contact avec notre équipe' },
    { id: 4, title: 'Entretien Final', desc: 'Rencontre avec le manager' },
    { id: 5, title: 'Décision', desc: 'Réponse définitive sous 48h' }
  ];

  const handleInputChange = (field, value) => {
    setApplicationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (file) => {
    setApplicationData(prev => ({
      ...prev,
      cv: file
    }));
  };

  const nextStep = () => {
    if (applicationStep < 3) {
      setApplicationStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (applicationStep > 1) {
      setApplicationStep(prev => prev - 1);
    }
  };

  const submitApplication = () => {
    // Simulate submission
    setApplicationStatus(prev => ({
      ...prev,
      status: 'submitted',
      submittedAt: new Date()
    }));
    setShowApplicationForm(false);
    setShowTracker(true);
  };

  const ApplicationForm = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900/95 to-indigo-900/95 backdrop-blur-xl rounded-3xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Candidature Express
            </h2>
            <button 
              onClick={() => setShowApplicationForm(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="flex items-center space-x-2 mb-2">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  step <= applicationStep 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                    : 'bg-white/10 text-gray-400'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-1 mx-2 rounded-full transition-all duration-300 ${
                    step < applicationStep ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-white/10'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm">
            {applicationStep === 1 && "Informations personnelles"}
            {applicationStep === 2 && "Expérience professionnelle"}
            {applicationStep === 3 && "Motivation et disponibilité"}
          </p>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {applicationStep === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Prénom</label>
                  <input
                    type="text"
                    value={applicationData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                  <input
                    type="text"
                    value={applicationData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                    placeholder="Votre nom"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={applicationData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                  placeholder="votre.email@exemple.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
                <input
                  type="tel"
                  value={applicationData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                  placeholder="+33 6 XX XX XX XX"
                />
              </div>
            </div>
          )}

          {applicationStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expérience professionnelle</label>
                <select
                  value={applicationData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                >
                  <option value="">Sélectionnez votre expérience</option>
                  <option value="0-1">0-1 ans d'expérience</option>
                  <option value="1-3">1-3 ans d'expérience</option>
                  <option value="3-5">3-5 ans d'expérience</option>
                  <option value="5+">Plus de 5 ans d'expérience</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CV (optionnel)</label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-blue-400/50 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-400 mb-2">Glissez votre CV ou cliquez pour sélectionner</p>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label
                    htmlFor="cv-upload"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer transition-colors inline-block"
                  >
                    Choisir un fichier
                  </label>
                  {applicationData.cv && (
                    <p className="text-green-400 mt-2 text-sm">✓ {applicationData.cv.name}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {applicationStep === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Qu'est-ce qui vous motive à nous rejoindre ?</label>
                <textarea
                  value={applicationData.motivation}
                  onChange={(e) => handleInputChange('motivation', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300 resize-none"
                  placeholder="Parlez-nous de vos motivations, objectifs et ce qui vous attire dans cette opportunité..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Disponibilité</label>
                <select
                  value={applicationData.availability}
                  onChange={(e) => handleInputChange('availability', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 outline-none transition-all duration-300"
                >
                  <option value="">Sélectionnez votre disponibilité</option>
                  <option value="immediate">Immédiatement</option>
                  <option value="1-week">Dans 1 semaine</option>
                  <option value="2-weeks">Dans 2 semaines</option>
                  <option value="1-month">Dans 1 mois</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-between">
          <button
            onClick={prevStep}
            disabled={applicationStep === 1}
            className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center ${
              applicationStep === 1 
                ? 'bg-gray-600/30 text-gray-400 cursor-not-allowed' 
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            <ArrowLeft size={16} className="mr-2" />
            Précédent
          </button>
          
          {applicationStep < 3 ? (
            <button
              onClick={nextStep}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl transition-all duration-300 flex items-center"
            >
              Suivant
              <ArrowRight size={16} className="ml-2" />
            </button>
          ) : (
            <button
              onClick={submitApplication}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 rounded-xl transition-all duration-300 flex items-center"
            >
              <Send size={16} className="mr-2" />
              Envoyer ma candidature
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const ApplicationTracker = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900/95 to-indigo-900/95 backdrop-blur-xl rounded-3xl border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
                Suivi de Candidature
              </h2>
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>ID: {applicationStatus.applicationId}</span>
                <span>•</span>
                <span>Soumise le {applicationStatus.submittedAt.toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
            <button 
              onClick={() => setShowTracker(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="p-6">
          <div className="relative">
            {applicationSteps.map((step, index) => (
              <div key={step.id} className="flex items-start mb-8 last:mb-0">
                <div className="flex flex-col items-center mr-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                    step.id <= applicationStatus.step
                      ? step.id === applicationStatus.step
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white animate-pulse'
                        : 'bg-green-500 text-white'
                      : 'bg-white/10 text-gray-400'
                  }`}>
                    {step.id < applicationStatus.step ? (
                      <CheckCircle size={20} />
                    ) : step.id === applicationStatus.step ? (
                      <AlertCircle size={20} className="animate-pulse" />
                    ) : (
                      step.id
                    )}
                  </div>
                  {index < applicationSteps.length - 1 && (
                    <div className={`w-1 h-16 mt-2 rounded-full transition-all duration-300 ${
                      step.id < applicationStatus.step ? 'bg-green-500' : 'bg-white/10'
                    }`} />
                  )}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className={`font-semibold mb-1 transition-colors ${
                    step.id <= applicationStatus.step ? 'text-white' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{step.desc}</p>
                  {step.id === applicationStatus.step && (
                    <div className="mt-2 px-3 py-1 bg-blue-500/20 rounded-full inline-block">
                      <span className="text-blue-400 text-xs font-medium">En cours</span>
                    </div>
                  )}
                  {step.id < applicationStatus.step && (
                    <div className="mt-2 px-3 py-1 bg-green-500/20 rounded-full inline-block">
                      <span className="text-green-400 text-xs font-medium">Terminé</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Next Steps */}
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl border border-white/10">
            <h3 className="font-semibold text-blue-300 mb-3">Prochaines étapes</h3>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-center">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                Nous vous contacterons sous 48h pour l'étape suivante
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                Préparez vos questions sur l'entreprise et le poste
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                Consultez vos emails régulièrement
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm mb-4">Des questions sur votre candidature ?</p>
            <div className="flex justify-center space-x-4">
              <a 
                href="tel:4384069650" 
                className="flex items-center px-4 py-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors"
              >
                <Phone size={16} className="mr-2" />
                Nous appeler
              </a>
              <a 
                href="mailto:contact@groupemosaik.com" 
                className="flex items-center px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
              >
                <Mail size={16} className="mr-2" />
                Nous écrire
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 text-white overflow-hidden">
      {/* Modals */}
      {showApplicationForm && <ApplicationForm />}
      {showTracker && <ApplicationTracker />}

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-1">
                <img 
                  src="https://groupemosaik.com/wp-content/uploads/2022/02/cropped-logo-74x74.png" 
                  alt="Groupe Mosaik Logo" 
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  GROUPE MOSAIK
                </span>
                <div className="text-xs text-gray-400">Professionnels de la vente et du service à la clientèle</div>
              </div>
            </div>
            
            <div className="hidden md:flex space-x-8">
              {['Accueil', 'Opportunités', 'Témoignages', 'Rejoignez-nous'].map((item) => (
                <a key={item} href="#" className="hover:text-blue-400 transition-colors duration-300 relative group">
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
              ))}
              <button 
                onClick={() => setShowTracker(true)}
                className="px-4 py-2 bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-400/30 rounded-lg hover:bg-green-600/30 transition-colors text-sm"
              >
                Suivi Candidature
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 ${isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden bg-black/30 backdrop-blur-lg`}>
          <div className="px-4 py-4 space-y-4">
            {['Accueil', 'Opportunités', 'Témoignages', 'Rejoignez-nous'].map((item) => (
              <a key={item} href="#" className="block hover:text-blue-400 transition-colors duration-300">
                {item}
              </a>
            ))}
            <button 
              onClick={() => setShowTracker(true)}
              className="block text-left hover:text-blue-400 transition-colors duration-300"
            >
              Suivi Candidature
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
          </div>
        </div>

        <div className={`relative z-10 text-center max-w-5xl mx-auto px-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="mb-6">
            <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full border border-blue-400/30 text-blue-300 text-sm font-medium">
              🚀 Groupe Mosaik recrute à Montréal
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent">
            Devenez Expert en
            <br />
            <span className="text-blue-400">Vente & Service Client</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Rejoignez les professionnels du marketing de quartier à Montréal. 
            Développez des relations durables avec les clients et construisez votre carrière dans un environnement innovant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowApplicationForm(true)}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center"
            >
              <Target className="mr-2" size={20} />
              Postuler Maintenant
            </button>
            <button className="px-8 py-4 border-2 border-white/30 rounded-full font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center">
              <Phone size={20} className="mr-2" />
              Appel Découverte : 438-406-9650
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown size={32} className="text-white/60" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-blue-300">Notre Impact</h2>
            <p className="text-gray-400">Des résultats concrets qui prouvent notre efficacité</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "15+", label: "Années d'expérience" },
              { number: "95%", label: "Clients satisfaits" },
              { number: "24h", label: "Réactivité moyenne" },
              { number: "100%", label: "Approche personnalisée" }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Pourquoi Rejoindre Groupe Mosaik ?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nous sommes les professionnels de la vente et du service à la clientèle. 
              Rejoignez une équipe qui offre des solutions efficaces et rentables pour développer votre potentiel entrepreneurial.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-blue-400/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://groupemosaik.com/wp-content/uploads/2025/01/46d8af93-6969-4d37-b19d-def1b956207e.jpeg" 
                  alt="Ciblage clientèle" 
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-blue-300">
                Nous Ciblons la Clientèle
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Nous utilisons les technologies les plus avancées qui existent sur le marché afin d'être le plus optimal à la satisfaction de nos clients.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-green-400/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://groupemosaik.com/wp-content/uploads/2025/01/d3560566-4952-4111-a780-74e8ac005e8a.jpeg" 
                  alt="Travail en équipe" 
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-green-300">
                Travail en Équipe
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Permet de favoriser le développement des compétences, de limiter les risques en cas d'absence et de développer l'intelligence collective.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://groupemosaik.com/wp-content/uploads/2025/01/ba6cd270-f546-47e3-bd99-8835e17bb657.jpeg" 
                  alt="Stratégie digitale" 
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-purple-300">
                Stratégie Digitale Gagnante
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Nous travaillons selon un plan d'action mené sur les différents supports digitaux tel que le web et le mobile.
              </p>
            </div>

            {opportunities.slice(0, 1).map((opportunity, index) => (
              <div 
                key={index} 
                className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-blue-400/50 transition-all duration-500 hover:transform hover:scale-105"
              >
                <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                  {opportunity.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-blue-300">
                  {opportunity.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {opportunity.desc}
                </p>
              </div>
            ))}

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-yellow-400/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="mb-6 overflow-hidden rounded-xl">
                <img 
                  src="https://groupemosaik.com/wp-content/uploads/2022/02/TIC-formation-salaries.jpeg" 
                  alt="Formation professionnelle" 
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-yellow-300">
                Formation Continue
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Programme de formation complet et personnalisé pour développer vos compétences en vente et service clientèle.
              </p>
            </div>

            <div className="group bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-pink-400/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-8 h-8 text-pink-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-pink-300">
                Flexibilité & Réactivité
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Nous sommes très réactifs aux besoins et aux questions de nos clients. Gérez votre emploi du temps selon vos objectifs.
              </p>
            </div>
          </div>

          {/* Benefits List */}
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-3xl p-8 backdrop-blur-lg border border-white/10">
            <h3 className="text-2xl font-bold mb-8 text-center text-blue-300">Ce Que Nous Offrons</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Témoignages de Réussite
            </h2>
            <p className="text-xl text-gray-300">
              Découvrez les parcours inspirants de nos entrepreneurs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-green-400/50 transition-all duration-300 relative"
              >
                <div className="absolute top-4 right-4 bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold">
                  {testimonial.earnings}
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-blue-300">{testimonial.name}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16 bg-black/20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="text-2xl font-bold mb-8 text-blue-300">Notre Approche</h3>
          <div className="flex justify-center items-center space-x-8 opacity-70">
            <div className="text-lg font-semibold text-white">MARKETING DE QUARTIER</div>
            <div className="w-1 h-12 bg-white/30"></div>
            <div className="text-lg font-semibold text-blue-400">VENTE DIRECTE</div>
            <div className="w-1 h-12 bg-white/30"></div>
            <div className="text-lg font-semibold text-green-400">SERVICE CLIENTÈLE</div>
          </div>
          <p className="text-gray-400 mt-6 max-w-2xl mx-auto">
            Nous adoptons une démarche commerciale simple : nous discutons directement avec les clients 
            et leur offrons des renseignements personnalisés pour des résultats immédiats et mesurables.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Changer Votre Vie ?
          </h2>
          <p className="text-xl mb-8 opacity-90 leading-relaxed">
            Ne laissez pas passer cette opportunité unique de devenir entrepreneur et de construire votre succès financier
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowApplicationForm(true)}
              className="px-8 py-4 bg-white text-blue-600 rounded-full font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center"
            >
              <Zap className="mr-2" size={20} />
              Candidature Express
            </button>
            <button className="px-8 py-4 border-2 border-white rounded-full font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center">
              <Phone size={20} className="mr-2" />
              Entretien Téléphonique
            </button>
          </div>
          <p className="text-sm mt-4 opacity-75">Réponse sous 24h garantie • Entretien gratuit et sans engagement</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur-lg py-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
                  <img 
                    src="https://groupemosaik.com/wp-content/uploads/2022/02/cropped-logo-74x74.png" 
                    alt="Groupe Mosaik Logo" 
                    className="w-full h-full object-contain rounded"
                  />
                </div>
                <span className="font-bold">GROUPE MOSAIK</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">
                Agence de marketing de quartier
              </p>
              <p className="text-gray-500 text-xs">
                Partenaire officiel Winco & Vidéotron
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-blue-300">Opportunités</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Représentant Commercial</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Manager d'Équipe</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Formation Continue</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-blue-300">Contact Recrutement</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center"><Phone size={14} className="mr-2" /> 438-406-9650</li>
                <li className="flex items-center"><Mail size={14} className="mr-2" /> contact@groupemosaik.com</li>
                <li className="flex items-center"><MapPin size={14} className="mr-2" /> 1305 Rue Mazurette, Montréal, QC H4N 1G8</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-blue-300">Suivez Notre Succès</h4>
              <div className="flex space-x-4 mb-4">
                {['LinkedIn', 'Facebook', 'Instagram'].map((social) => (
                  <a key={social} href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                    {social}
                  </a>
                ))}
              </div>
              <div className="bg-green-500/20 px-3 py-2 rounded-lg">
                <p className="text-green-400 text-xs font-semibold">✅ Nous recrutons activement</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 Groupe Mosaik. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;